import { Gem, MapPin } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { Badge } from '@/slw/shared/ui/badge';
import { cn, formatMoney } from '@/slw/shared/lib/utils';
import { RecentFeaturedListing } from '@/slw/entities/dealer/sell-your-car';

interface RecentFeaturedCardProps {
  dealerStatus: boolean;
  listing: RecentFeaturedListing;
}

export function RecentFeaturedCard({ listing, dealerStatus }: RecentFeaturedCardProps) {
  const router = useRouter();
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  const getInitialSrc = () => {
    if (listing.mainImageUrl?.startsWith('http')) {
      return listing.mainImageUrl;
    }

    return '/images/common/no-image-available.jpg';
  };

  const src = currentSrc || getInitialSrc();

  const handleError = () => {
    setCurrentSrc('/images/common/no-image-available.png');
  };

  const isRemoteImage = listing.mainImageUrl?.startsWith('http');

  return (
    <div
      className='bg-[#F8F8F7] rounded-md 
       cursor-pointer flex flex-col p-2 pb-4 gap-4 relative group'
      tabIndex={0}
      role='button'
      aria-label={`View details for ${listing.title}`}
      onClick={() => router.push(`/listing/${listing.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') router.push(`/listing/${listing.id}`);
      }}
    >
      <div className='relative aspect-[306/172] w-full bg-gray-100 rounded-[6px] overflow-hidden'>
        <Image
          src={src}
          alt={listing.title}
          className='object-cover object-center group-hover:scale-105 transition-transform duration-200'
          fill
          quality={90}
          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          priority={false}
          onError={handleError}
          unoptimized={!isRemoteImage}
        />
        {listing.isPremiumSpotlight && (
          <span
            className={cn(
              'absolute top-3 left-0 text-white text-sm px-4 py-1.5 leading-[120%] flex items-center gap-2',
              dealerStatus ? 'bg-[#FF2D55]' : 'bg-[#FF9500]',
            )}
          >
            {dealerStatus && <Gem size={16} className='inline-block' />}
            Premium Spotlight
          </span>
        )}
      </div>
      <div className='flex flex-col gap-2 px-1 pb-1'>
        <div>
          <Badge
            variant='outline'
            className='bg-primary text-white rounded-md text-sm border-none leading-[120%] font-normal py-0.75'
          >
            Pre War
          </Badge>
        </div>
        <div className='text-lg font-bold text-primary leading-[160%]'>
          {listing.price ? formatMoney(listing.price, true) : ''}
        </div>
        <div className='flex items-center justify-between text-sm font-normal leading-[150%]'>
          <span className='flex items-center gap-2'>
            <MapPin size={16} />
            {listing.city ? `${listing.city},${listing.stateName}` : listing.stateName}
          </span>
          <span className='font-mono'>{listing.listingId}</span>
        </div>
      </div>
    </div>
  );
}
